import { useState } from "react";
import { Link as LinkIcon, Linkedin, Facebook, Mail, MessageCircle, Check } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { absoluteUrl } from "@/lib/seo-config";

/**
 * D5 — UTM-tagged share buttons. Each destination gets its own utm_source so
 * the user can attribute traffic in GA4 / our funnel dashboard.
 */
function buildShareUrl(path, source) {
  const base = absoluteUrl(path);
  const u = new URL(base);
  u.searchParams.set("utm_source", source);
  u.searchParams.set("utm_medium", "share");
  u.searchParams.set("utm_campaign", "blog");
  return u.toString();
}

export default function ShareButtons({ path, title }) {
  const [copied, setCopied] = useState(false);
  const safeTitle = title || "Read this from Ashlan Clinic";

  const onClick = (destination, href) => {
    track("share_click", { share_destination: destination, page_path: path });
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  const onCopy = async () => {
    const link = buildShareUrl(path, "copy-link");
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success("Link copied — share it anywhere.");
      track("share_click", { share_destination: "copy_link", page_path: path });
      setTimeout(() => setCopied(false), 2200);
    } catch {
      window.prompt("Copy this link:", link);
    }
  };

  const targets = [
    {
      key: "linkedin",
      label: "LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildShareUrl(path, "linkedin"))}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(buildShareUrl(path, "facebook"))}`,
    },
    {
      key: "x",
      label: "Post on X",
      // lucide-react doesn't have a Twitter/X icon in older versions — use text glyph.
      Icon: null,
      glyph: "X",
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(buildShareUrl(path, "x"))}&text=${encodeURIComponent(safeTitle)}`,
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: `https://wa.me/?text=${encodeURIComponent(safeTitle + " — " + buildShareUrl(path, "whatsapp"))}`,
    },
    {
      key: "email",
      label: "Email",
      Icon: Mail,
      href: `mailto:?subject=${encodeURIComponent(safeTitle)}&body=${encodeURIComponent(buildShareUrl(path, "email"))}`,
    },
  ];

  return (
    <div data-testid="share-buttons" className="mt-12 pt-8 border-t border-[#e5e7eb]">
      <div className="flex flex-wrap items-center gap-3">
        <span className="ash-eyebrow mr-2">Share this</span>
        <button
          type="button"
          onClick={onCopy}
          data-testid="share-copy-link"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#e5e7eb] text-[#080808] text-xs uppercase tracking-[0.18em] font-bold hover:border-[#3A7A8C] hover:text-[#3A7A8C] transition-colors"
        >
          {copied ? <Check size={14} /> : <LinkIcon size={14} />}
          {copied ? "Copied" : "Copy Link"}
        </button>
        {targets.map(({ key, label, Icon, glyph, href }) => (
          <button
            key={key}
            type="button"
            onClick={() => onClick(key, href)}
            data-testid={`share-${key}`}
            aria-label={label}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#e5e7eb] text-[#080808] text-xs uppercase tracking-[0.18em] font-bold hover:border-[#3A7A8C] hover:text-[#3A7A8C] transition-colors"
          >
            {Icon ? <Icon size={14} /> : <span className="text-sm font-extrabold leading-none">{glyph}</span>}
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
